import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Button,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssignmentIcon from "@mui/icons-material/Assignment";
import SchoolIcon from "@mui/icons-material/School";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import color from "../../components/utils/Colors";
import {
  getEnrolledCourses,
  getUserActivity,
  getDataFromTable,
} from "../../services/services";
import { useUser } from "../../services/Context/UserContext";

interface CourseProgress {
  id: string;
  title: string;
  progress: number;
  hours: number;
  instructor: string;
}

interface RecentActivity {
  id: string;
  action: string;
  course: string;
  date: string;
  type: "started" | "completed" | "viewed" | "commented" | string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [courses, setCourses] = useState<CourseProgress[]>([]);
  const [activities, setActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);

    Promise.allSettled([getEnrolledCourses(user.id), getUserActivity(user.id)])
      .then(([enrolledResult, activityResult]) => {
        // Enrolled courses
        if (enrolledResult.status === "fulfilled") {
          const data: any[] = (enrolledResult.value as any)?.data?.data ?? [];
          Promise.all(
            data.map(async (item: any) => {
              let courseRes;
              if (item.courseId) {
                courseRes = await getDataFromTable("course", {
                  id: item.courseId,
                });
              }
              const course = courseRes?.data?.data?.[0] || item?.course || {};
              return {
                id: String(item.courseId ?? course?.id ?? ""),
                title: course?.title ?? course?.courseName ?? "Course",
                progress: item?.progress ?? 0,
                hours: course?.duration ? parseInt(course.duration) : 0,
                instructor: course?.teachersName ?? "",
              };
            }),
          ).then((mapped) => {
            setCourses(mapped);
          });
        }

        // User activity
        if (activityResult.status === "fulfilled") {
          const actData: any[] =
            (activityResult.value as any)?.data?.data ?? [];
          const mappedActivity: RecentActivity[] = actData
            .slice(0, 10)
            .map((item: any) => ({
              id: String(item?.id ?? ""),
              action: item?.activityAction ?? "Activity",
              course: item?.activityType ?? "",
              date: item?.createdAt
                ? new Date(item.createdAt).toLocaleDateString()
                : "Recently",
              type: (item?.activityType ?? "viewed") as RecentActivity["type"],
            }));
          setActivities(mappedActivity);
        }
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  const totalProgress =
    courses.length > 0
      ? Math.round(
          courses.reduce((sum, c) => sum + c.progress, 0) / courses.length,
        )
      : 0;
  const completedCourses = courses.filter((c) => c.progress >= 100).length;
  const totalHours = courses.reduce((sum, c) => sum + (c.hours || 0), 0);

  const getActivityColor = (type: string) => {
    switch (type) {
      case "completed":
        return "#4caf50";
      case "started":
        return color.firstColor;
      case "commented":
        return color.thirdColor;
      case "viewed":
        return "#ff9800";
      default:
        return "#999";
    }
  };

  const StatCard = ({
    icon: Icon,
    value,
    label,
    color: bgColor,
  }: {
    icon: React.ComponentType<any>;
    value: string | number;
    label: string;
    color: string;
  }) => (
    <Card
      sx={{
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
        borderRadius: "12px",
      }}
    >
      <CardContent sx={{ padding: "24px", textAlign: "center" }}>
        <Box
          sx={{
            background: bgColor,
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 12px",
            color: "white",
          }}
        >
          <Icon sx={{ fontSize: "24px" }} />
        </Box>
        <Typography
          variant="h4"
          sx={{
            color: color.textColor,
            fontWeight: "bold",
            marginBottom: "4px",
          }}
        >
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: "#999" }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{ minHeight: "100vh", background: "transparent", paddingY: "40px" }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ marginBottom: "40px" }}>
          <Typography
            variant="h4"
            sx={{
              color: color.textColor,
              fontWeight: "bold",
              marginBottom: "8px",
            }}
          >
            Welcome Back{user?.name ? `, ${user.name}` : ""}! 👋
          </Typography>
          <Typography variant="body1" sx={{ color: "#666" }}>
            Here's your learning overview for today
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress sx={{ color: color.firstColor }} />
          </Box>
        ) : (
          <>
            {/* Stats Cards */}
            <Grid container spacing={3} sx={{ marginBottom: "40px" }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={TrendingUpIcon}
                  value={`${totalProgress}%`}
                  label="Overall Progress"
                  color={color.firstColor}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={SchoolIcon}
                  value={courses.length}
                  label="Courses Enrolled"
                  color={color.thirdColor}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={EmojiEventsIcon}
                  value={completedCourses}
                  label="Completed"
                  color="#4caf50"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  icon={AssignmentIcon}
                  value={`${totalHours}h`}
                  label="Total Hours"
                  color="#ff9800"
                />
              </Grid>
            </Grid>

            {/* Main Content Grid */}
            <Grid container spacing={3}>
              {/* Active Courses */}
              <Grid item xs={12} lg={8}>
                <Card
                  sx={{
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    borderRadius: "12px",
                    marginBottom: "20px",
                  }}
                >
                  <CardContent sx={{ padding: "24px" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: color.textColor,
                        fontWeight: "bold",
                        marginBottom: "20px",
                      }}
                    >
                      Active Courses
                    </Typography>

                    {courses.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "16px",
                        }}
                      >
                        {courses.map((course) => (
                          <Box
                            key={course.id}
                            sx={{
                              paddingBottom: "16px",
                              borderBottom: "1px solid #f0f0f0",
                              "&:last-child": {
                                borderBottom: "none",
                                paddingBottom: 0,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "start",
                                marginBottom: "8px",
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: color.textColor,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {course.title}
                                </Typography>
                                {course.instructor && (
                                  <Typography
                                    variant="body2"
                                    sx={{ color: "#999", marginTop: "4px" }}
                                  >
                                    By {course.instructor}
                                  </Typography>
                                )}
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: color.firstColor,
                                  fontWeight: "bold",
                                }}
                              >
                                {course.progress}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={course.progress}
                              sx={{
                                height: "8px",
                                borderRadius: "4px",
                                backgroundColor: "#e0e0e0",
                                marginBottom: "8px",
                                "& .MuiLinearProgress-bar": {
                                  backgroundColor: color.firstColor,
                                },
                              }}
                            />
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              {course.hours > 0 && (
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999" }}
                                >
                                  {course.hours} hours total
                                </Typography>
                              )}
                              <Button
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                sx={{
                                  color: color.firstColor,
                                  textTransform: "none",
                                  fontWeight: "bold",
                                  "&:hover": { background: color.secondColor },
                                }}
                                onClick={() =>
                                  navigate(`/open-course/${course.id}`)
                                }
                              >
                                Continue
                              </Button>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          color: "#999",
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        No active courses. Start learning now!
                      </Typography>
                    )}

                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        marginTop: "20px",
                        borderColor: color.firstColor,
                        color: color.firstColor,
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          borderColor: color.forthColor,
                          color: color.forthColor,
                        },
                      }}
                      onClick={() => navigate("/learning")}
                    >
                      View All Courses
                    </Button>
                  </CardContent>
                </Card>

                {/* Recent Activity */}
                <Card
                  sx={{
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    borderRadius: "12px",
                  }}
                >
                  <CardContent sx={{ padding: "24px" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: color.textColor,
                        fontWeight: "bold",
                        marginBottom: "20px",
                      }}
                    >
                      Recent Activity
                    </Typography>

                    {activities.length > 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        {activities.map((activity) => (
                          <Box
                            key={activity.id}
                            sx={{
                              display: "flex",
                              gap: "12px",
                              paddingBottom: "12px",
                              borderBottom: "1px solid #f0f0f0",
                              "&:last-child": {
                                borderBottom: "none",
                                paddingBottom: 0,
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: "8px",
                                height: "8px",
                                borderRadius: "50%",
                                backgroundColor: getActivityColor(
                                  activity.type,
                                ),
                                marginTop: "6px",
                                flexShrink: 0,
                              }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: color.textColor,
                                  fontWeight: "500",
                                }}
                              >
                                {activity.action}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ color: "#999" }}
                              >
                                {activity.course && `${activity.course} • `}
                                {activity.date}
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography
                        sx={{
                          color: "#999",
                          textAlign: "center",
                          padding: "20px",
                        }}
                      >
                        No recent activity
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* Sidebar */}
              <Grid item xs={12} lg={4}>
                {/* Quick Actions */}
                <Card
                  sx={{
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    borderRadius: "12px",
                  }}
                >
                  <CardContent sx={{ padding: "24px" }}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: color.textColor,
                        fontWeight: "bold",
                        marginBottom: "16px",
                      }}
                    >
                      Quick Actions
                    </Typography>

                    <Button
                      fullWidth
                      variant="contained"
                      sx={{
                        background: color.firstColor,
                        color: "white",
                        textTransform: "none",
                        fontWeight: "bold",
                        marginBottom: "12px",
                        "&:hover": { background: color.forthColor },
                      }}
                      onClick={() => navigate("/course")}
                    >
                      Explore Courses
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        borderColor: color.firstColor,
                        color: color.firstColor,
                        textTransform: "none",
                        fontWeight: "bold",
                        marginBottom: "12px",
                        "&:hover": {
                          borderColor: color.forthColor,
                          color: color.forthColor,
                        },
                      }}
                      onClick={() => navigate("/wishlist")}
                    >
                      View Wishlist
                    </Button>

                    <Button
                      fullWidth
                      variant="outlined"
                      sx={{
                        borderColor: color.firstColor,
                        color: color.firstColor,
                        textTransform: "none",
                        fontWeight: "bold",
                        "&:hover": {
                          borderColor: color.forthColor,
                          color: color.forthColor,
                        },
                      }}
                      onClick={() => navigate("/profile")}
                    >
                      View Profile
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </>
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;
